/**
 * Restaurant Selector Component
 *
 * Multi-select component for selecting restaurants based on user's access.
 */

import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { useAuth } from '@/src/hooks/useAuth'
import { httpClient } from '@/src/@core/lib/httpClient'
import { getUserRestaurants } from '@/src/utils/restaurantAccessUtils'

interface Restaurant {
  id: number
  name: string
  code?: string
  state?: string
}

interface RestaurantSelectorProps {
  value: number[]
  onChange: (restaurantIds: number[]) => void
  useAllRestaurants: boolean
  onUseAllChange: (useAll: boolean) => void
  disabled?: boolean
}

export const RestaurantSelector: React.FC<RestaurantSelectorProps> = ({
  value,
  onChange,
  useAllRestaurants,
  onUseAllChange,
  disabled = false
}) => {
  const auth = useAuth()
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoading(true)
        setError(null)

        const userRoleScope = auth.user?.role?.scope

        // If user has restaurant scope, use restaurants from user object
        if (userRoleScope === 'restaurant') {
          const userRestaurants = getUserRestaurants(auth.user)
          const formattedRestaurants: Restaurant[] = userRestaurants.map((r: any) => ({
            id: r.id || r.restaurantId,
            name: r.restaurantName || r.name || `Restaurant ${r.id || r.restaurantId}`,
            code: r.code,
            state: r.state
          }))
          setRestaurants(formattedRestaurants)
        } else {
          // For country scope or other scopes, fetch all accessible restaurants
          const response = await httpClient.get<{ data: any[] }>('/restaurants/open-and-future-opening')
          const restaurantsData = Array.isArray(response.data) ? response.data : response.data.data || []
          const formattedRestaurants: Restaurant[] = restaurantsData.map((r: any) => ({
            id: r.id,
            name: r.restaurantName || r.name || `Restaurant ${r.id}`,
            code: r.code,
            state: r.state
          }))
          setRestaurants(formattedRestaurants)
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load restaurants')
        console.error('Error fetching restaurants:', err)
      } finally {
        setLoading(false)
      }
    }

    if (auth.user) {
      fetchRestaurants()
    }
  }, [auth.user])

  const handleSelectChange = (event: any) => {
    const selectedIds = event.target.value as number[]
    onChange(selectedIds)
  }

  const renderValue = (selected: number[]) => {
    if (selected.length === 0) {
      return 'Select restaurants...'
    }
    if (selected.length === 1) {
      const restaurant = restaurants.find(r => r.id === selected[0])

      return restaurant?.name || `Restaurant ${selected[0]}`
    }

    return `${selected.length} restaurants selected`
  }

  if (loading) {
    return (
      <Box display='flex' alignItems='center' gap={2}>
        <CircularProgress size={20} />
        <Typography variant='body2' color='text.secondary'>
          Loading restaurants...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Typography variant='body2' color='error'>
        {error}
      </Typography>
    )
  }

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox checked={useAllRestaurants} onChange={e => onUseAllChange(e.target.checked)} disabled={disabled} />
        }
        label='All Accessible Restaurants'
        sx={{ mb: 2 }}
      />

      {!useAllRestaurants && (
        <FormControl fullWidth>
          <InputLabel>Select Restaurants</InputLabel>
          <Select
            multiple
            value={value}
            onChange={handleSelectChange}
            renderValue={renderValue}
            disabled={disabled || loading}
            label='Select Restaurants'
          >
            {restaurants.map(restaurant => (
              <MenuItem key={restaurant.id} value={restaurant.id}>
                <Checkbox checked={value.includes(restaurant.id)} />
                <ListItemText
                  primary={restaurant.name}
                  secondary={
                    restaurant.code
                      ? `Code: ${restaurant.code}${restaurant.state ? ` • ${restaurant.state}` : ''}`
                      : undefined
                  }
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!useAllRestaurants && value.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {value.map(id => {
            const restaurant = restaurants.find(r => r.id === id)

            return (
              <Chip
                key={id}
                label={restaurant?.name || `Restaurant ${id}`}
                onDelete={() => onChange(value.filter(v => v !== id))}
                size='small'
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}
